import React, { useEffect, useState } from 'react';
import { inputs } from '@ecosystem/newron-design-system';
import { FilterProps } from './types';
import { Formatting } from '../data/formatting';
import { DataType, FormatHint } from '../types';
import { failsRoundingCriteria } from './utils';

const { NumberInput } = inputs;

function NumericRangeFilter({
  definition: { name, label, min: minVal, max: maxVal, roundTo, currency },
  values,
  disabled,
  onChange,
}: FilterProps) {
  const labelValues = values[0]?.label.split('-');
  const isMinValueEmpty = labelValues?.[0] === undefined || labelValues?.[0] === '';
  const isMaxValueEmpty = labelValues?.[1] === undefined || labelValues?.[1] === '';
  const minValue = isMinValueEmpty ? null : Number(labelValues[0]);
  const maxValue = isMaxValueEmpty ? null : Number(labelValues[1]);
  const [min, setMin] = useState(minValue);
  const [max, setMax] = useState(maxValue);

  const [formatted, setFormatted] = useState<string | null>(null);

  useEffect(() => {
    if (roundTo) {
      const formatted = Formatting.format(roundTo, {
        dataType: DataType.Integer,
        formatHint: FormatHint.Money,
        currency,
      });
      setFormatted(formatted);
    }
  }, [roundTo, formatted, currency]);

  useEffect(
    function handleClearClicked() {
      if (!values.length) {
        setMin(null);
        setMax(null);
      } else {
        setMin(minValue);
        setMax(maxValue);
      }
    },
    [values.length, minValue, maxValue]
  );

  const handleMinBlur = () => {
    if (min < 0) {
      setMin(null);
      if (max !== null) {
        onChange(name, [{ value: `-${max ?? ''}`, label: `-${max ?? ''}` }]);
      } else {
        onChange(name, []);
      }
      return;
    }

    if (min && min >= 0 && max >= 0 && min > max) {
      const text = `${min}-`;
      setMax(null);
      onChange(name, [{ value: text, label: text }]);
    } else {
      if (min === null && max === null) {
        onChange(name, []);
      } else {
        const text = `${min ?? ''}-${max ?? ''}`;
        onChange(name, [{ value: text, label: text }]);
      }
    }
  };

  const handleMaxBlur = () => {
    if (max < 0) {
      setMax(null);
      if (min !== null) {
        onChange(name, [{ value: `${min ?? ''}-`, label: `${min ?? ''}-` }]);
      } else {
        onChange(name, []);
      }
      return;
    } else if (max === 0 && min) {
      const text = '-0';
      setMin(null);
      onChange(name, [{ value: text, label: text }]);
      return;
    }

    if (min >= 0 && max && max >= 0 && min > max) {
      const text = `-${max}`;
      setMin(null);
      onChange(name, [{ value: text, label: text }]);
    } else {
      if (min === null && max === null) {
        onChange(name, []);
      } else {
        const text = `${min ?? ''}-${max ?? ''}`;
        onChange(name, [{ value: text, label: text }]);
      }
    }
  };

  return (
    <div className="relative" data-test-id={label}>
      <div className={`text-md font-bold flex-grow text-heading ${!formatted ? 'pb-2' : ''}`}>{label}</div>
      {formatted && (
        <span className="text-sm text-lighter pb-2">{`Enter prices that can be divided by ${formatted}`}</span>
      )}
      <div className="flex items-start gap-2">
        <NumberInput
          helperText={`Min: ${minVal ?? 0}`}
          value={min}
          minValue={0}
          disabled={disabled}
          onChange={setMin}
          onBlur={handleMinBlur}
          hasError={failsRoundingCriteria(min, roundTo)}
          step={Number(roundTo)}
        />
        <span className="pt-3">—</span>
        <NumberInput
          helperText={`Max: ${maxVal ?? 0}`}
          value={max}
          minValue={0}
          disabled={disabled}
          onChange={setMax}
          onBlur={handleMaxBlur}
          hasError={failsRoundingCriteria(max, roundTo)}
          step={Number(roundTo)}
        />
      </div>
    </div>
  );
}

export { NumericRangeFilter };
